import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  WorkflowOfflineService,
  WorkflowHttpErrorsService,
  WorkflowService,
} from '@core/services/workflow';
import {
  RequestWorkflowsData,
  RequestWorkflowWithNodes,
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS,
  SyncWorkflowFormErrorResponse,
  WorkflowExternal,
  WorkflowFormValidationError,
  WorkflowFormSubmissionExternal,
  WorkflowNodeExternal,
  WorkflowNodeFormWithPopulatedFieldsExternal,
} from '../types';
import { TuiButton, TuiScrollbar } from '@taiga-ui/core';
import { ReactiveFormsModule } from '@angular/forms';
import { WorkflowHeaderComponent } from './components/workflow-header/workflow-header.component';
import { WorkflowNodeFormComponent } from './components/workflow-node-form/workflow-node-form.component';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestService } from '@core/services/request/request.service';
import { XAlertService } from '@core/services/x-alert.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { WorkflowsTranslations } from '@core/types';
import { WorkflowPendingWorkService } from '@core/services/workflow/workflow-pending-work.service';
import { DeviceDetectorService } from '@core/services/device-detector/device-detector.service';
import { WorkflowFinalPageComponent } from '../workflow-final-page/workflow-final-page.component';
import { WorkflowScreenStateComponent } from '../workflow-screen-state/workflow-screen-state.component';
import { TranslatePipe } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AsyncPipe } from '@angular/common';
import { WorkflowStepperComponent } from '@features/requests';

@Component({
  selector: 'x-workflow-wizard',
  imports: [
    TuiButton,
    ReactiveFormsModule,
    WorkflowHeaderComponent,
    WorkflowNodeFormComponent,
    WorkflowFinalPageComponent,
    TranslatePipe,
    TuiScrollbar,
    AsyncPipe,
    WorkflowStepperComponent,
    WorkflowScreenStateComponent,
  ],
  templateUrl: './workflow-wizard.component.html',
  styleUrl: './workflow-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowWizardComponent implements OnInit {
  // Inputs
  public workflowId = input.required<string>();
  public nodeId = input.required<string>();
  public formId = input.required<string>();
  // Из data маршрута workflows/final (withComponentInputBinding)
  public isFinalRoute = input(false);

  // Services
  private readonly requestService = inject(RequestService);
  private readonly workflowService = inject(WorkflowService);
  private readonly workflowOfflineService = inject(WorkflowOfflineService);
  private readonly pendingWorkService = inject(WorkflowPendingWorkService);
  private readonly workflowHttpErrorsService = inject(
    WorkflowHttpErrorsService,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  readonly $screenState = this.workflowService.$screenState;
  readonly $pendingWork = this.workflowService.$pendingWork;

  retryFailedSync(): void {
    void this.workflowService.retryFailedSync();
  }
  readonly deviceService = inject(DeviceDetectorService);

  // ViewChild
  @ViewChild(WorkflowFinalPageComponent)
  private finalPageComponent?: WorkflowFinalPageComponent;

  // Signals
  public $requestsWorkflowData = signal<RequestWorkflowsData | null>(null);
  public $currentWorkflow = signal<RequestWorkflowWithNodes | null>(null);
  public $currentWorkflowName = signal<WorkflowExternal['name']>('');
  public $currentNode = signal<WorkflowNodeExternal | null>(null);
  public $currentNodeForm =
    signal<WorkflowNodeFormWithPopulatedFieldsExternal | null>(null);
  public $currentFormSubmission = signal<WorkflowFormSubmissionExternal | null>(
    null,
  );
  public $returnLink = signal('');

  // Computed signals
  readonly $request = this.requestService.$request;
  public $baseRoute = computed(
    () => `/requests/${this.$request()?.requestNumber}`,
  );
  public $currentNodeId = computed(() => parseInt(this.nodeId(), 10));
  public $currentFormId = computed(() => parseInt(this.formId(), 10));
  public $isFinalPage = computed(
    () => this.isFinalRoute() && !!this.$requestsWorkflowData(),
  );

  public $allFormsCompleted = computed(() => {
    const data = this.$requestsWorkflowData();

    if (!data || data.requestsWorkflows.length === 0) return false;

    // Используем готовый метод из сервиса
    return this.workflowService.areAllWorkflowNodesCompleted();
  });

  constructor() {
    effect(() => {
      // Read all reactive dependencies to trigger effect on changes
      this.nodeId();
      this.formId();
      this.workflowId();
      const wfData = this.workflowService.$requestsWorkflowData();

      if (!wfData || !this.$request()) return;

      // Update local state and derived entities
      this.$requestsWorkflowData.set(wfData);
      this.updateCurrentEntities();
      this.updateReturnLink();
    });
  }

  ngOnInit(): void {
    this.workflowService.refreshEntities();
  }

  /**
   * Update current workflow, node, and form based on route params
   */
  private updateCurrentEntities(): void {
    const data = this.$requestsWorkflowData();

    if (!data || this.isFinalRoute()) return;

    const workflow =
      data.requestsWorkflows.find(w => w.id === this.workflowId()) ?? null;
    const node =
      workflow?.nodes.find(n => n.id === this.$currentNodeId()) ?? null;
    // Форма должна принадлежать текущей ноде, а не быть найдена глобально по id
    const form = node
      ? (data.workflowNodesForms.find(
          f => f.id === this.$currentFormId() && f.workflowNodeId === node.id,
        ) ?? null)
      : null;

    if (!workflow || !node || !form) {
      // Битый маршрут (неизвестный регламент/нода/форма) — не показываем
      // финальную страницу, а возвращаем к списку регламентов
      console.warn('Workflow route does not match request workflows', {
        workflowId: this.workflowId(),
        nodeId: this.nodeId(),
        formId: this.formId(),
      });
      void this.router.navigate([this.$baseRoute(), 'workflows'], {
        replaceUrl: true,
      });

      return;
    }

    this.$currentWorkflow.set(workflow);
    this.$currentNode.set(node);
    this.$currentWorkflowName.set(workflow.workflowVersion.workflow.name || '');
    this.$currentNodeForm.set(form);

    // Find submission for current form
    this.$currentFormSubmission.set(
      data.submissions.find(
        s =>
          s.requestWorkflowId === workflow.id &&
          s.workflowNodeFormId === this.$currentFormId(),
      ) ?? null,
    );
  }

  /**
   * Update return link based on current node
   */
  private updateReturnLink(): void {
    const returnLink = this.workflowService.getReturnLink(
      this.workflowId(),
      this.$currentNodeId(),
      this.$currentFormId(),
      this.$baseRoute(),
      this.$isFinalPage(),
    );

    this.$returnLink.set(returnLink);
  }

  /**
   * Navigate to the next form / node or navigate to the next workflow or navigate to the final page
   */
  public sync(): void {
    const nextRoute = this.workflowService.getNextRouteAfterSync(
      this.workflowId(),
      this.$currentNodeId(),
      this.$currentFormId(),
      this.$baseRoute(),
    );

    if (nextRoute) {
      void this.router.navigate([nextRoute]);
    }
  }

  /**
   * Handle sync event for the final page
   */
  public async syncFinalPage(): Promise<void> {
    const requestNumber = this.$request()!.requestNumber;

    // Завершение запрещено, пока по заявке есть неотправленные формы или фото
    let pendingCount: number;

    try {
      pendingCount =
        await this.pendingWorkService.countForRequest(requestNumber);
    } catch (error) {
      // Очередь на устройстве не прочиталась — на ней могут быть неотправленные данные
      console.error('Failed to read pending workflow tasks:', error);
      this.finalPageComponent?.resetSubmitting();
      this.xAlertService.error(
        this.outkeepTranslateService.instant(
          WorkflowsTranslations.PendingStateUnknown,
        ),
      );

      return;
    }

    if (pendingCount > 0) {
      this.finalPageComponent?.resetSubmitting();
      this.xAlertService.warn(
        this.outkeepTranslateService.instant(
          WorkflowsTranslations.CompleteBlockedPending,
          { count: pendingCount },
        ),
      );

      return;
    }

    // Тот же operationId используется и при офлайн-постановке завершения в очередь
    const operationId = crypto.randomUUID();

    this.workflowService
      .completeRequestWorkflows(requestNumber, operationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.workflowService.refreshEntities();
          this.xAlertService.success(
            'Рабочие процессы заявки успешно завершены.',
            '',
            3000,
          );
          void this.router.navigate([`${this.$baseRoute()}/summary`]);

          // Сбрасываем состояние загрузки
          if (this.finalPageComponent) {
            this.finalPageComponent.resetSubmitting();
          }
        },
        error: (err: HttpErrorResponse) => {
          // Сбрасываем состояние загрузки при ошибке
          if (this.finalPageComponent) {
            this.finalPageComponent.resetSubmitting();
          }

          if (err.status === 0) {
            void this.queueOfflineCompletion(operationId);

            return;
          }

          const errorResponse = err.error as SyncWorkflowFormErrorResponse;

          // Обработка ошибок валидации
          if (
            errorResponse?.failureReason ===
            SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.validationErrors
          ) {
            const validationErrors =
              errorResponse?.context?.validationErrors || [];

            if (
              this.finalPageComponent &&
              Array.isArray(validationErrors) &&
              validationErrors.length > 0
            ) {
              this.finalPageComponent.handleValidationErrors(validationErrors);

              this.workflowHttpErrorsService.showCompleteValidationErrors(
                validationErrors as WorkflowFormValidationError[],
              );

              return;
            }
          }
          this.workflowHttpErrorsService.showCompleteHttpError(err);
        },
      });
  }

  /**
   * Навигация на финальную страницу с автоматическим сабмитом
   */
  /**
   * Нет сети при завершении: сохраняем задачу на устройстве и уходим с мастера
   * только после успешной записи. Если записать не удалось — остаёмся на финальной странице.
   */
  private async queueOfflineCompletion(operationId: string): Promise<void> {
    const requestNumber = this.$request()!.requestNumber;

    try {
      await this.workflowOfflineService.addCompletionToQueue({
        requestNumber,
        timestamp: Date.now(),
        operationId,
      });
    } catch (error) {
      console.error('Failed to queue offline workflows completion:', error);
      this.xAlertService.error(
        this.outkeepTranslateService.instant(
          WorkflowsTranslations.OfflineCompletionNotSaved,
        ),
      );

      return;
    }

    this.xAlertService.warn(
      this.outkeepTranslateService.instant(
        WorkflowsTranslations.OfflineCompletionQueued,
      ),
      '',
      6000,
    );
    void this.router.navigate([`${this.$baseRoute()}/summary`]);
  }

  goToFinalPage(): void {
    void this.router.navigate([`${this.$baseRoute()}/workflows/final`], {
      state: { autoSubmit: true },
    });
  }
}
